/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const RESIZING_CLASS = 'is-resizing-horizontally';

export const useHorizontalSplitter = (containerRef, initialRightPaneWidth, constraints = {}) => {
  const [rightPaneWidth, setRightPaneWidth] = useState(initialRightPaneWidth);
  const animationFrameId = useRef(null);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      document.body.classList.add(RESIZING_CLASS);
      const startX = e.clientX;
      const startWidth = rightPaneWidth;

      const handleMouseMove = (moveEvent) => {
        moveEvent.preventDefault();

        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }

        animationFrameId.current = requestAnimationFrame(() => {
          if (!containerRef.current) {
            return;
          }

          const parentWidth = containerRef.current.offsetWidth;
          const maxRightPaneWidth = parentWidth - (constraints.leftPaneMinWidth || 0);
          const minRightPaneWidth = constraints.rightPaneMinWidth || 0;

          let newWidth = startWidth - (moveEvent.clientX - startX);

          newWidth = Math.max(newWidth, minRightPaneWidth);
          newWidth = Math.min(newWidth, maxRightPaneWidth);

          setRightPaneWidth(newWidth);
        });
      };

      const handleMouseUp = () => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }
        document.body.classList.remove(RESIZING_CLASS);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [rightPaneWidth, containerRef, constraints]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      setRightPaneWidth((currentWidth) => {
        const parentWidth = container.offsetWidth;
        const maxRightPaneWidth = parentWidth - (constraints.leftPaneMinWidth || 0);

        if (currentWidth > maxRightPaneWidth) {
          return maxRightPaneWidth;
        }

        return currentWidth;
      });
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, constraints.leftPaneMinWidth]);

  useEffect(() => {
    return () => {
      document.body.classList.remove(RESIZING_CLASS);
    };
  }, []);

  return { width: rightPaneWidth, handleMouseDown };
};
